<template>
  <div
    class="related-products w-full flex flex-col gap-4"
  >
    <div :class="{'lg:hidden': hasBanner}">
      <ProductAnchorTitle
        :title="title"
        :header-class="headerClass"
        :is-small="isTitleSmall"
      />
      <p v-if="description">
        {{ description }}
      </p>
      <CustomLink
        v-if="seeAllLinkSlug && seeAllLinkText"
        :url="seeAllLinkSlug"
        :text="seeAllLinkText"
        :type="LINK_TYPE.InheritColor"
      >
        <span
          class="flex gap-2.5 items-center"
          :class="linkClass"
        >
          <span>{{ seeAllLinkText }}</span>
          <span class="iconify svg--chevron-right pt-1" />
        </span>
      </CustomLink>
    </div>
    <ProductSlider
      data-testid="related-products-carousel"
      :class="productSliderClasses"
      :slider-items="sliderItems"
      :size="size"
      :has-controls-outside-container="hasControlsOutsideContainer"
      :is-full-width="isFullWidth"
      :prev-arrow-class="prevArrowClass"
      :next-arrow-class="nextArrowClass"
      :has-arrows="hasArrows"
    >
      <template #default="{ currentItem }">
        <div
          v-if="currentItem.hasBanner"
          class="related-products__banner"
        >
          <ProductSliderBanner
            :banner-header="banner.header"
            :banner-description="banner.description"
            :banner-button-link="banner.buttonLink"
            :banner-button-text="banner.buttonText"
            :banner-image="banner.image"
          />
        </div>
        <ProductSliderItem
          v-else
          :product="currentItem"
          :price-guarantee-url="priceGuaranteeUrl"
          :parcel-delivery-date="parcelDeliveryDate"
          :freight-delivery-date="freightDeliveryDate"
          :size="size"
          :product-box-type-tag="productBoxTypeTag"
          @product-slider-item-clicked="productSliderItemClicked"
        />
      </template>
    </ProductSlider>
    <div
      v-if="showBannerButton"
      class="lg:hidden px-4"
    >
      <CustomButton
        type="secondary"
        :text="banner.buttonText"
        :url="banner.buttonLink"
      />
    </div>
  </div>
</template>

<script lang="ts">

import type { ProductVariant } from '~~/types/commercetools';
import { LINK_TYPE } from '~~/types/components/Link/LinkType';
import type { ProductCardSize } from '~~/constants/productCardSize';
import { PRODUCT_CARD_SIZE } from '~~/constants/productCardSize';

import type { ProductSliderBanner as ProductSliderBannerType } from '~~/types/components/ProductSliderBanner/ProductSliderBanner';
import type { EventSourceLocation } from '~~/types/integrations/tagManager/TagManagerIntegration';

export default defineComponent({
  name: 'ProductSliderContainer',

  props: {
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    products: {
      type: Array as PropType<ProductVariant[]>,
      default: () => {

        return [];
      
      },
    },
    seeAllLinkText: {
      type: String,
      default: null,
    },
    seeAllLinkSlug: {
      type: String,
      default: null,
    },
    hasBanner: {
      type: Boolean,
      default: false,
    },
    banner: {
      type: Object as PropType<ProductSliderBannerType>,
      default: () => {

        return {};
      
      },
    },
    hasControlsOutsideContainer: {
      type: Boolean,
      default: false,
    },
    isFullWidth: {
      type: Boolean,
      default: false,
    },
    isProductZone: {
      type: Boolean,
      default: false,
    },
    headerClass: {
      type: String,
      default: 'text-EXPONDO-black',
    },
    linkClass: {
      type: String,
      default: 'text-EXPONDO-secondary',
    },
    prevArrowClass: {
      type: String,
      default: 'left-0',
    },
    nextArrowClass: {
      type: String,
      default: 'right-0',
    },
    size: {
      type: String as PropType<ProductCardSize>,
      default: PRODUCT_CARD_SIZE.RESPONSIVE,
      validator: (value: ProductCardSize) => {

        return Object.values(PRODUCT_CARD_SIZE).includes(value);
      
      },
    },
    productBoxTypeTag: {
      type: String as PropType<EventSourceLocation>,
      default: undefined,
    },
    hasArrows: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['product-clicked'],
  setup(props, { emit }) {

    const { priceGuaranteeUrl } = useRouteBestPriceGuarantee();
    const { search: searchProductDeliveryDate, parcelDeliveryDate, freightDeliveryDate } = useProductDeliveryDate(true);

    onMounted(() => {

      searchProductDeliveryDate();
    
    });

    const sliderItems = computed(() => props.hasBanner ? [{ hasBanner: true }, ...props.products] : props.products);
    const productZoneClasses = 'bg-EXPONDO-white py-2.5 px-2 flex-1 lg:border-2 lg:border-EXPONDO-grey-10';
    const productSliderClasses = computed(() => props.isProductZone ? productZoneClasses : '');
    const showBannerButton = computed(() => props.hasBanner && props.banner.buttonLink && props.banner.buttonText);

    const productSliderItemClicked = (sku: string) => {

      emit('product-clicked', sku);
    
    };

    const isTitleSmall = computed(() => props.size === PRODUCT_CARD_SIZE.TILE_MINI);

    return {
      LINK_TYPE,
      priceGuaranteeUrl,
      parcelDeliveryDate,
      freightDeliveryDate,
      sliderItems,
      productSliderClasses,
      showBannerButton,
      productSliderItemClicked,
      PRODUCT_CARD_SIZE,
      isTitleSmall,
    };
  
  },
});


</script>

<style lang="scss" scoped>
@use "~/assets/scss/helpers/_breakpoints" as breakpoints;
.section {
  --section-content-margin: 0;

  margin-top: var(--spacer-xl);
}

:deep(*) {
  .simple-carousel-item:has(.related-products__banner) {
    display: none;

    @include breakpoints.for-desktop {
      display: flex;
    }
  }
}
</style>
